import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { X, Check } from 'lucide-react';
import { format, addYears } from 'date-fns';

const fmt = d => format(d, 'yyyy-MM-dd');
const today = fmt(new Date());
const nextYear = fmt(addYears(new Date(), 1));

export default function PolicyModal({ policy, clientId, onClose }) {
  const { addPolicy, updatePolicy, clients, companies, currentUser } = useApp();
  const isEdit = !!policy;

  const [form, setForm] = useState(policy || {
    clientId: clientId || '',
    number: 'POL-' + new Date().getFullYear() + '-' + String(Date.now()).slice(-4),
    company: '',
    type: 'auto',
    coverage: '',
    premium: '',
    currency: 'UYU',
    startDate: today,
    endDate: nextYear,
    notes: '',
    assetData: {},
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setAsset = (k, v) => setForm(f => ({ ...f, assetData: { ...f.assetData, [k]: v } }));

  const submit = async (e) => {
    e.preventDefault();
    if (isEdit) await updatePolicy(policy.id, form);
    else await addPolicy({ ...form, status: 'active' });
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700 }}>{isEdit ? 'Editar póliza' : 'Nueva póliza'}</h2>
          <button onClick={onClose} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px' }}><X size={14} /></button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            {/* Cliente (solo si no viene prefijado) */}
            {!clientId && (
              <div className="form-group">
                <label className="form-label">Cliente *</label>
                <select value={form.clientId} onChange={e => set('clientId', e.target.value)} required>
                  <option value="">Seleccionar cliente...</option>
                  {[...clients].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es')).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            )}

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">N° de póliza *</label>
                <input value={form.number} onChange={e => set('number', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Compañía aseguradora *</label>
                <select value={form.company} onChange={e => set('company', e.target.value)} required>
                  <option value="">Seleccionar...</option>
                  {companies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Ramo / Tipo *</label>
                <select value={form.type} onChange={e => { set('type', e.target.value); set('assetData', {}); }}>
                  <optgroup label="🚗 Vehículos">
                    <option value="auto">🚗 Automóvil</option>
                    <option value="moto">🏍️ Motocicleta</option>
                    <option value="truck">🚛 Transporte / Camión</option>
                    <option value="fleet">🚌 Flota vehicular</option>
                    <option value="boat">⛵ Embarcación / Náutico</option>
                    <option value="tractor">🚜 Tractor / Maquinaria agrícola</option>
                  </optgroup>
                  <optgroup label="🏠 Inmuebles y Propiedades">
                    <option value="home">🏠 Hogar / Apartamento</option>
                    <option value="commercial">🏢 Comercio / Local</option>
                    <option value="building">🏗️ Edificio / Propiedad horizontal</option>
                    <option value="agro">🌾 Agro / Rural</option>
                    <option value="construction">🔨 Construcción / Todo riesgo constructor</option>
                    <option value="glass">🪟 Vidrios y cristales</option>
                  </optgroup>
                  <optgroup label="👤 Personas">
                    <option value="life">❤️ Vida</option>
                    <option value="life_term">💙 Vida término / Temporal</option>
                    <option value="accident">🩺 Accidentes personales</option>
                    <option value="health">💊 Salud / Gastos médicos</option>
                    <option value="disability">♿ Incapacidad / Invalidez</option>
                    <option value="funeral">🌹 Sepelio / Funeral</option>
                  </optgroup>
                  <optgroup label="🏢 Empresas y Responsabilidad">
                    <option value="liability">⚖️ Responsabilidad civil</option>
                    <option value="liability_professional">📋 Responsabilidad civil profesional</option>
                    <option value="liability_products">📦 Responsabilidad por productos</option>
                    <option value="technical">⚙️ Todo riesgo equipos / Electrónico</option>
                    <option value="cargo">🚢 Transporte de mercadería</option>
                    <option value="fire">🔥 Incendio industrial</option>
                    <option value="fidelity">🤝 Fidelidad / Deshonestidad de empleados</option>
                    <option value="surety">📜 Caución / Garantía</option>
                    <option value="cyber">💻 Riesgo cibernético / Cyber</option>
                    <option value="directors">👔 Directores y Gerentes (D&O)</option>
                    <option value="workers">👷 Accidentes de trabajo / ART</option>
                  </optgroup>
                  <optgroup label="🌍 Especiales">
                    <option value="travel">✈️ Asistencia al viajero</option>
                    <option value="pet">🐾 Mascotas</option>
                    <option value="credit">💳 Crédito</option>
                    <option value="art">🖼️ Obras de arte / Colecciones</option>
                    <option value="other">📄 Otro</option>
                  </optgroup>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Cobertura</label>
                <input value={form.coverage} onChange={e => set('coverage', e.target.value)} placeholder="Ej: Todo riesgo, RC..." />
              </div>
              <div className="form-group">
                <label className="form-label">Prima (monto)</label>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select value={form.currency || 'UYU'} onChange={e => set('currency', e.target.value)} style={{ width: 80, flexShrink: 0 }}>
                    <option value="UYU">$ UYU</option>
                    <option value="USD">U$S</option>
                  </select>
                  <input type="number" value={form.premium} onChange={e => set('premium', Number(e.target.value))} placeholder="0" style={{ flex: 1 }} />
                </div>
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Inicio de vigencia</label>
                <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Fin de vigencia</label>
                <input type="date" value={form.endDate} onChange={e => set('endDate', e.target.value)} />
              </div>
            </div>

            {/* Asset fields */}
            {/* Asset fields — Vehículos */}
            {['auto','moto','truck','fleet','boat','tractor'].includes(form.type) && (
              <>
                <div style={{ fontSize: 11, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '4px 0 -4px' }}>
                  {{'auto':'🚗','moto':'🏍️','truck':'🚛','fleet':'🚌','boat':'⛵','tractor':'🚜'}[form.type]} Datos del bien asegurado
                </div>
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Marca</label><input value={form.assetData.brand || ''} onChange={e => setAsset('brand', e.target.value)} placeholder="Ej: Toyota" /></div>
                  <div className="form-group"><label className="form-label">Modelo</label><input value={form.assetData.model || ''} onChange={e => setAsset('model', e.target.value)} placeholder="Ej: Corolla" /></div>
                  <div className="form-group"><label className="form-label">Año</label><input value={form.assetData.year || ''} onChange={e => setAsset('year', e.target.value)} placeholder="Ej: 2022" /></div>
                  <div className="form-group"><label className="form-label">Matrícula / Patente</label><input value={form.assetData.plate || ''} onChange={e => setAsset('plate', e.target.value)} placeholder="Ej: SBF 1234" /></div>
                  <div className="form-group"><label className="form-label">VIN / Chasis</label><input value={form.assetData.vin || ''} onChange={e => setAsset('vin', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Motor</label><input value={form.assetData.engine || ''} onChange={e => setAsset('engine', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Padrón</label><input value={form.assetData.padron || ''} onChange={e => setAsset('padron', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Color</label><input value={form.assetData.color || ''} onChange={e => setAsset('color', e.target.value)} /></div>
                </div>
              </>
            )}

            {/* Asset fields — Inmuebles */}
            {['home','commercial','building','agro','construction'].includes(form.type) && (
              <>
                <div style={{ fontSize: 11, fontFamily: 'var(--font-display)', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '4px 0 -4px' }}>
                  {{'home':'🏠','commercial':'🏢','building':'🏗️','agro':'🌾','construction':'🔨'}[form.type]} Datos del bien asegurado
                </div>
                <div className="form-grid">
                  <div className="form-group"><label className="form-label">Dirección del bien</label><input value={form.assetData.address || ''} onChange={e => setAsset('address', e.target.value)} /></div>
                  <div className="form-group"><label className="form-label">Tipo</label>
                    <select value={form.assetData.type || ''} onChange={e => setAsset('type', e.target.value)}>
                      <option value="">Seleccionar...</option>
                      <option>Apartamento</option><option>Casa</option><option>Oficina</option>
                      <option>Local comercial</option><option>Depósito</option><option>Planta industrial</option>
                      <option>Campo / Establecimiento rural</option>
                    </select>
                  </div>
                  <div className="form-group"><label className="form-label">Superficie</label><input value={form.assetData.area || ''} onChange={e => setAsset('area', e.target.value)} placeholder="Ej: 75m²" /></div>
                  <div className="form-group"><label className="form-label">Valor asegurado</label><input value={form.assetData.value || ''} onChange={e => setAsset('value', e.target.value)} placeholder="USD" /></div>
                </div>
              </>
            )}

            <div className="form-group">
              <label className="form-label">Notas</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} />
            </div>

            {currentUser?.role === 'admin' && (
              <div onClick={() => set('critical', !form.critical)}
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start', gap: 12, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', width: '100%', boxSizing: 'border-box', background: form.critical ? 'var(--amber-dim)' : 'var(--bg-input)', border: `1px solid ${form.critical ? 'var(--amber)' : 'var(--border)'}` }}>
                <span style={{ width: 20, height: 20, minWidth: 20, flexShrink: 0, borderRadius: 4, border: `2px solid ${form.critical ? 'var(--amber)' : 'var(--text-muted)'}`, background: form.critical ? 'var(--amber)' : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {form.critical && <Check size={13} color="#1a1a1a" strokeWidth={3} />}
                </span>
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: form.critical ? 'var(--amber)' : 'var(--text-primary)' }}>
                    Marcar como cuenta crítica
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Aparece destacada en el Calendario con cuenta regresiva
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn btn-secondary">Cancelar</button>
            <button type="submit" className="btn btn-primary">{isEdit ? 'Guardar' : 'Crear póliza'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
